import _ from 'lodash'
import {
    type ContentSuggestionOptions,
    type EntitySuggestionResult,
    type OutlineSuggestionResult,
    type PageSuggestionResults,
    type LinkGenerationOutlineSuggestionResult,
    dataUtils
} from '@wix/document-manager-extensions'
import {getReportableFromError} from '@wix/document-manager-utils'
import type {
    Callback,
    Callback1,
    Callback2,
    Pointer,
    PS,
    SerializedCompStructure,
    OutlineSuggestionResultForStructure,
    SectionSuggestionOnErrorParams,
    ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    IdMap,
    CompRef,
    CompRefWithHint
} from '@wix/document-services-types'
import {initAITools} from '../aiTools/aiTools'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {BuilderAiInjectionResultWithIdMapAndOldValues} from '@wix/document-manager-extensions/dist/src/extensions/ai/aiBuilderGeneration'
import routers from '../routers/routers'
import {VIEW_MODES} from '@wix/santa-core-utils/dist/ts/coreUtils/core/constants'
import experiment from 'experiment-amd'
import type {AiApi} from '@wix/document-manager-extensions/dist/src/extensions/ai/aiContentExtension'

interface OutlineSuggestionOnSuccessParams {
    suggestionResults: OutlineSuggestionResult
}

interface OutlineSuggestionOnErrorParams {
    contentServerError?: string | object
    originalError: Error
    tokenUsage: any[]
}

interface LinkGenerationSuggestionOnSuccessParams {
    suggestionResults: LinkGenerationOutlineSuggestionResult
}

interface LinkGenerationOnErrorParams {
    originalError: Error
}

interface EntireSiteSuggestionOnErrorParams {
    originalError: Error
    pageSuggestions: PageSuggestionResults[]
}

async function getSuggestedOutline(
    ps: PS,
    pageId: string,
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<OutlineSuggestionOnSuccessParams>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>,
    options?: ContentSuggestionOptions,
    toneOfVoice?: string
) {
    try {
        const suggestion = await ps.extensionAPI.ai.content.getSuggestedOutline(
            pageId,
            category,
            businessName,
            description,
            options,
            toneOfVoice
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete(suggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentGetSuggestedOutlineError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

function applyOutline(
    ps: PS,
    suggestionResults: OutlineSuggestionResult,
    pageId: string,
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<OutlineSuggestionOnSuccessParams>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>
) {
    try {
        ps.extensionAPI.ai.content.applyOutline(suggestionResults, pageId)
        onSuccess?.({
            suggestionResults
        })
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentApplyOutlineError'
        })
        onError?.({
            originalError: e,
            tokenUsage: suggestionResults?.completionMetadata?.tokenUsage
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}
const getRouterInnerRoutesWrapper = async (ps: PS, routerId: string, pageId: string): Promise<any> => {
    return new Promise(resolve => {
        routers.getRouterInnerRoutes(ps, routerId, pageId, resolve, false)
    })
}
const getDefaultDynamicPagesRoutes = async ps => {
    ps.extensionAPI.logger.interactionStarted('linkGenerationGetDynamicPages')
    let counter = 0
    const result = await ps.siteAPI.getAllPagesIds(false).reduce(async (previousPromise, pageId: string) => {
        const res = await previousPromise
        const pageRef = ps.pointers.getPointer(pageId, VIEW_MODES.DESKTOP)
        const routerRef = routers.getRouterRef.byPage(ps, pageRef)
        if (routerRef) {
            const {id} = routers.get.byRef(ps, routerRef)
            counter++
            const innerRoutes = await getRouterInnerRoutesWrapper(ps, id, pageId)
            if (innerRoutes.length > 0) {
                const firstInnerRouter = _.head(innerRoutes)
                res[pageId] = {
                    innerRoute: (firstInnerRouter as any).url,
                    routerId: id
                }
            }
        }
        return res
    }, Promise.resolve({}))
    ps.extensionAPI.logger.interactionEnded('linkGenerationGetDynamicPages', {counter})
    return result
}

function applyBuilderInjection(
    ps: PS,
    builderAiInjectionResultWithIdMapAndOldValues: BuilderAiInjectionResultWithIdMapAndOldValues,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string,
    onSuccess?: Callback1<OutlineSuggestionOnSuccessParams>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>
) {
    try {
        ps.extensionAPI.ai.content.applyBuilderInjection(builderAiInjectionResultWithIdMapAndOldValues)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AITextInjectionError'
        })
        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

function applyBuilderImageInjectionSG(
    ps: PS,
    imageGenerationServerlessResponseWithIdMapAndOldUrlMap: ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string,
    photoTheme: string,
    onSuccess?: Callback1<ImageGenerationServerlessResponseWithIdMapAndOldUrlMap>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>
) {
    try {
        onSuccess?.(imageGenerationServerlessResponseWithIdMapAndOldUrlMap)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIBuilderImageInjectionError'
        })

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

async function getComponentInjectionForBuilder(
    ps: PS,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string,
    onSuccess?: Callback1<any>,
    onError?: Callback1<any>
) {
    try {
        const injectionSuggestionWithIdMapAndOldValues =
            await ps.extensionAPI.ai.content.getComponentInjectionForBuilder(
                componentPointer,
                siteType,
                businessName,
                siteDescription,
                userRequest
            )
        onSuccess?.({
            suggestionResults: injectionSuggestionWithIdMapAndOldValues
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(injectionSuggestionWithIdMapAndOldValues)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AITextOutlineError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

async function uploadAndInjectImages(
    ps: PS,
    idMap: IdMap,
    data: Record<string, string>,
    oldUrlMap: Record<string, string>,
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        await ps.extensionAPI.ai.content.uploadAndInjectImages({idMap, data, oldUrlMap})
        onSuccess?.()
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIUploadAndInjectImagesError'
        })
        ps.extensionAPI.logger.captureError(newError)
        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

async function getComponentInjectionForBuilderImage(
    ps: PS,
    compRefsWithHints: CompRefWithHint[],
    siteType: string,
    businessName: string,
    siteDescription: string,
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        await (ps.extensionAPI.ai as AiApi).content.getImageInjectionForBuilder(
            compRefsWithHints,
            siteType,
            businessName,
            siteDescription
        )

        onSuccess?.()
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIUrlSuggestionForBuilderImageError'
        })
        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

async function getComponentInjectionForBuilderImageSG(
    ps: PS,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string,
    photoTheme: string,
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        const injectionSuggestionWithIdMapAndOldUrlMap = await (
            ps.extensionAPI.ai as AiApi
        ).content.getImageInjectionForBuilderSG(
            componentPointer,
            siteType,
            businessName,
            siteDescription,
            userRequest,
            photoTheme
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete(injectionSuggestionWithIdMapAndOldUrlMap)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIUrlSuggestionForBuilderImageSGError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

async function getPageLinksOutline(ps: PS, pageId: string, onSuccess?: Callback1<any>, onError?: Callback1<any>) {
    try {
        let pagesLinksOutlines
        if (experiment.isOpen('dm_linkGenerationWithDynamicPages')) {
            const defaultInnerRouterMap = await getDefaultDynamicPagesRoutes(ps)
            pagesLinksOutlines = await ps.extensionAPI.ai.content.getSuggestedLinksToInject(pageId)
            pagesLinksOutlines.defaultInnerRouterMap = defaultInnerRouterMap
        } else {
            pagesLinksOutlines = await ps.extensionAPI.ai.content.getSuggestedLinksToInject(pageId)
        }

        ps.setOperationsQueue.asyncPreDataManipulationComplete(pagesLinksOutlines)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIGetPossibleLinksOutlineError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}
async function getPagesLinksOutline(ps: PS, pageIds: string[], onSuccess?: Callback1<any>, onError?: Callback1<any>) {
    try {
        let pagesLinksOutlines = {}
        let defaultInnerRouterMap = {}
        if (experiment.isOpen('dm_linkGenerationWithDynamicPages')) {
            defaultInnerRouterMap = await getDefaultDynamicPagesRoutes(ps)
            pagesLinksOutlines = await Promise.all(
                pageIds.map(pageId => ps.extensionAPI.ai.content.getSuggestedLinksToInject(pageId))
            )
        } else {
            pagesLinksOutlines = await Promise.all(
                pageIds.map(pageId => ps.extensionAPI.ai.content.getSuggestedLinksToInject(pageId))
            )
        }
        ps.setOperationsQueue.asyncPreDataManipulationComplete({defaultInnerRouterMap, pagesLinksOutlines})
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIGetPossibleLinksOutlineError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            contentServerError: e?.extras?.responseBody,
            originalError: e,
            tokenUsage: e?.extras?.tokenUsage
        })
    }
}

function generateLinksForOutline(
    ps: PS,
    pageLinksOutline: LinkGenerationOutlineSuggestionResult,
    pageId: string,
    onSuccess?: Callback1<LinkGenerationSuggestionOnSuccessParams>,
    onError?: Callback1<LinkGenerationOnErrorParams>
) {
    try {
        const {outline} = pageLinksOutline
        if (_.isEmpty(outline)) {
            onSuccess?.({
                suggestionResults: pageLinksOutline
            })
            return
        }
        ps.extensionAPI.ai.content.generateLinksForOutline(pageLinksOutline, pageId)
        onSuccess?.({
            suggestionResults: pageLinksOutline
        })
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AILinkGenerationCreationError'
        })
        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

export interface LinkGenerationResultWrapper {
    defaultInnerRouterMap: Record<string, any>
    pagesLinksOutlines: LinkGenerationOutlineSuggestionResult[]
}

function generatesLinksForOutline(
    ps: PS,
    res: LinkGenerationResultWrapper,
    pageIds,
    onSuccess?: Callback1<LinkGenerationSuggestionOnSuccessParams[]>,
    onError?: Callback1<LinkGenerationOnErrorParams[]>
) {
    const {defaultInnerRouterMap, pagesLinksOutlines} = res
    Promise.all(
        pagesLinksOutlines.map(pageLinkOutline => {
            return new Promise((resolve, reject) => {
                pageLinkOutline.defaultInnerRouterMap = defaultInnerRouterMap
                generateLinksForOutline(ps, pageLinkOutline, pageLinkOutline.pageId, resolve, reject)
            })
        })
    )
        // @ts-ignore
        // eslint-disable-next-line promise/prefer-await-to-then
        .then(onSuccess)
        // eslint-disable-next-line promise/prefer-await-to-then
        .catch(onError)
}

async function getSuggestedSiteOutlines(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<PageSuggestionResults[]>,
    onError?: Callback1<EntireSiteSuggestionOnErrorParams>,
    options?: ContentSuggestionOptions,
    toneOfVoice?: string
) {
    try {
        const suggestions = await ps.extensionAPI.ai.content.getSuggestedSiteOutlines(
            category,
            businessName,
            description,
            options,
            toneOfVoice
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete(suggestions)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentgetSuggestedSiteOutlinesError'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        onError?.({
            originalError: e,
            pageSuggestions: e?.extras?.pageSuggestionResults
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function getSuggestedOutlineByStructure(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    compStructure: SerializedCompStructure,
    sectionCategory?: string,
    onSuccess?: Callback2<SerializedCompStructure, OutlineSuggestionResultForStructure>,
    onError?: Callback1<SectionSuggestionOnErrorParams>,
    language?: string, //should be string of the language for example English,
    outlineCompIdToSectionNameOverrides: Record<string, string> = {}
) {
    try {
        const sectionPromptVersionOverride = ps.siteAPI.getQueryParam('sectionPromptVersionOverride')
        const sectionParamsVersionOverride = ps.siteAPI.getQueryParam('sectionParamsVersionOverride')
        const suggestionResults = await ps.extensionAPI.ai.content.getSuggestedOutlineByStructure(
            category,
            businessName,
            description,
            compStructure,
            sectionCategory,
            {promptVersionOverride: sectionPromptVersionOverride, paramsVersionOverride: sectionParamsVersionOverride},
            language,
            outlineCompIdToSectionNameOverrides
        )

        ps.extensionAPI.ai.content.applyOutlineToStructure(
            suggestionResults.contentSuggestion,
            suggestionResults.compStructure
        )
        onSuccess?.(suggestionResults.compStructure, suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'getSuggestedOutlinesByStructure'
        })

        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function getSuggestedOutlineByPageStructure(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    compStructure: SerializedCompStructure,
    onSuccess?: Callback1<EntitySuggestionResult>,
    onError?: Callback1<SectionSuggestionOnErrorParams>,
    language?: string, //should be string of the language for example English
    outlineCompIdToSectionNameOverrides: Record<string, string> = {}
) {
    try {
        const pagePromptVersionOverride = ps.siteAPI.getQueryParam('pagePromptVersionOverride')
        const pageParamsVersionOverride = ps.siteAPI.getQueryParam('pageParamsVersionOverride')
        const suggestionResults = await ps.extensionAPI.ai.content.getSuggestedOutlineByPageStructure(
            category,
            businessName,
            description,
            compStructure,
            {promptVersionOverride: pagePromptVersionOverride, paramsVersionOverride: pageParamsVersionOverride},
            language,
            outlineCompIdToSectionNameOverrides
        )
        ps.setOperationsQueue.asyncPreDataManipulationComplete({suggestionResults, compStructure})
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'getSuggestedOutlinesByStructure'
        })
        ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

interface SerializedSectionParams {
    section: SerializedCompStructure
    sectionName?: string
}

const getSectionIdToNameMap = (compStructures: SerializedSectionParams[]) => {
    const idToNameMap: Record<string, string> = {}
    const generatedIds = []
    for (const {section, sectionName} of compStructures) {
        if (sectionName) {
            if (!section.id) {
                const newId = dataUtils.generateItemIdWithPrefix('comp')
                section.id = newId
                generatedIds.push(newId)
            }
            idToNameMap[section.id] = sectionName
        }
    }

    return {idToNameMap, generatedIds}
}

const safeGetJsonLength = (obj: any) => {
    try {
        return JSON.stringify(obj).length
    } catch {
        return -1
    }
}

async function getSuggestedOutlineByStructures(
    ps: PS,
    category: string,
    businessName: string,
    description: string,
    serializedSectionParams: SerializedSectionParams[],
    onSuccess?: Callback2<SerializedCompStructure[], OutlineSuggestionResultForStructure>,
    onError?: Callback1<SectionSuggestionOnErrorParams>,
    language?: string //should be string of the language for example English
) {
    try {
        // this function can mutate the serializedSections, we clone it here to prevent unexpected behavior
        serializedSectionParams = deepClone(serializedSectionParams)

        const mergedStructure: Partial<SerializedCompStructure> = {
            componentType: 'mobile.core.components.Page',
            components: serializedSectionParams.map(({section}) => section)
        }

        const {idToNameMap, generatedIds} = getSectionIdToNameMap(serializedSectionParams)

        const pagePromptVersionOverride = ps.siteAPI.getQueryParam('pagePromptVersionOverride')
        const pageParamsVersionOverride = ps.siteAPI.getQueryParam('pageParamsVersionOverride')
        const suggestionResults = await ps.extensionAPI.ai.content.getSuggestedOutlineByPageStructure(
            category,
            businessName,
            description,
            mergedStructure,
            {promptVersionOverride: pagePromptVersionOverride, paramsVersionOverride: pageParamsVersionOverride},
            language,
            idToNameMap
        )

        if (suggestionResults.error) {
            throw suggestionResults.error
        }

        // sections ids may have been generated for outline creation, we need to omit these
        suggestionResults.contentSuggestion.idsToKeep = _.omit(
            suggestionResults.contentSuggestion.idsToKeep,
            generatedIds
        )

        ps.extensionAPI.ai.content.applyOutlineToPageStructure(
            suggestionResults.contentSuggestion,
            suggestionResults.compStructure
        )
        onSuccess?.(suggestionResults.compStructure.components, suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'getSuggestedOutlineByStructures',
            extras: {
                contentLength: safeGetJsonLength(serializedSectionParams)
            }
        })
        ps.extensionAPI.logger.captureError(newError)

        onError?.({
            originalError: e
        })
    }
}

async function applyOutlines(
    ps: PS,
    suggestionResults: PageSuggestionResults[],
    category: string,
    businessName: string,
    description: string,
    onSuccess?: Callback1<PageSuggestionResults[]>,
    onError?: Callback1<EntireSiteSuggestionOnErrorParams>
) {
    try {
        for (const pageResult of suggestionResults.filter(pageSuggestion => !pageSuggestion?.error)) {
            ps.extensionAPI.ai.content.applyOutline(pageResult.pageSuggestion, pageResult.pageId)
        }
        onSuccess?.(suggestionResults)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentApplyOutlinesForEntireSiteError'
        })
        onError?.({
            originalError: e,
            pageSuggestions: e?.extras?.pageSuggestionResults
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function applyOutlineToPage(
    ps: PS,
    suggestionData: {suggestionResults: EntitySuggestionResult},
    category: string,
    businessName: string,
    description: string,
    _compStructure: SerializedCompStructure,
    sectionCategory?: string,
    onSuccess?: Callback1<OutlineSuggestionResultForStructure>,
    onError?: Callback1<OutlineSuggestionOnErrorParams>
) {
    try {
        ps.extensionAPI.ai.content.applyOutline(
            suggestionData.suggestionResults.contentSuggestion,
            suggestionData.suggestionResults.entityId
        )
        onSuccess?.(suggestionData.suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'AIContentApplyOutlineError'
        })
        onError?.({
            originalError: e,
            tokenUsage: suggestionData.suggestionResults.contentSuggestion?.completionMetadata?.tokenUsage
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

async function applyOutlineToPageStructure(
    ps: PS,
    suggestionData: {suggestionResults: EntitySuggestionResult; compStructure: SerializedCompStructure},
    category: string,
    businessName: string,
    description: string,
    _compStructure: SerializedCompStructure,
    onSuccess?: Callback2<SerializedCompStructure, OutlineSuggestionResultForStructure>,
    onError?: Callback1<SectionSuggestionOnErrorParams>
) {
    try {
        const {suggestionResults, compStructure} = suggestionData
        ps.extensionAPI.ai.content.applyOutlineToPageStructure(suggestionResults.contentSuggestion, compStructure)
        onSuccess?.(compStructure, suggestionResults.contentSuggestion)
    } catch (e: any) {
        const newError = getReportableFromError(e, {
            errorType: 'applyOutlinesToStructureError'
        })
        onError?.({
            originalError: e
        })
        ps.extensionAPI.logger.captureError(newError)
    }
}

const processError = (ps: PS, e: any, errorType: string, onError?: Callback1<any>) => {
    const newError = getReportableFromError(e, {errorType})
    ps.setOperationsQueue.asyncPreDataManipulationComplete(null, newError as Error)

    ps.extensionAPI.logger.captureError(newError)

    onError?.({
        contentServerError: e?.extras?.responseBody,
        originalError: e,
        tokenUsage: e?.extras?.tokenUsage
    })
}

const onGenerateSimilarImagesSuccess = (ps: PS, componentPointers: CompRef[], onSuccess?: Callback) => {
    onSuccess?.()
}

async function generateSimilarImages(
    ps: PS,
    componentPointers: CompRef[],
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        await (ps.extensionAPI.ai as AiApi).images.generateSimilarImages(componentPointers)
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        processError(ps, e, 'GenerateSimilarImagesError', onError)
    }
}

const onMatchImagesToPageSuccess = (
    ps: PS,
    componentPointers: CompRef[],
    siteDescription: string,
    onSuccess?: Callback
) => {
    onSuccess?.()
}

async function matchImagesToPage(
    ps: PS,
    componentPointers: CompRef[],
    siteDescription: string,
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        await (ps.extensionAPI.ai as AiApi).images.matchImagesToPage(componentPointers, siteDescription)
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        processError(ps, e, 'MatchImagesToPageError', onError)
    }
}

const onFitImagesToFrameSuccess = (ps: PS, componentPointers: CompRef[], onSuccess?: Callback) => {
    onSuccess?.()
}

async function fitImagesToFrame(ps: PS, componentPointers: CompRef[], onSuccess?: Callback, onError?: Callback1<any>) {
    try {
        await (ps.extensionAPI.ai as AiApi).images.fitImagesToFrame(componentPointers)
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        processError(ps, e, 'FitImagesToFrameError', onError)
    }
}

const onRemoveImagesBackgroundSuccess = (ps: PS, componentPointers: CompRef[], onSuccess?: Callback) => {
    onSuccess?.()
}

async function removeImagesBackground(
    ps: PS,
    componentPointers: CompRef[],
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        await (ps.extensionAPI.ai as AiApi).images.removeImagesBackground(componentPointers)
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        processError(ps, e, 'RemoveImagesBackgroundError', onError)
    }
}

const onEditImagesSuccess = (ps: PS, componentPointers: CompRef[], userPrompt: string, onSuccess?: Callback) => {
    onSuccess?.()
}

async function editImages(
    ps: PS,
    componentPointers: CompRef[],
    userPrompt: string,
    onSuccess?: Callback,
    onError?: Callback1<any>
) {
    try {
        await (ps.extensionAPI.ai as AiApi).images.editImages(componentPointers, userPrompt)
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        processError(ps, e, 'EditImagesError', onError)
    }
}

const onUpscaleImagesSuccess = (ps: PS, componentPointers: CompRef[], onSuccess?: Callback) => {
    onSuccess?.()
}

async function upscaleImages(ps: PS, componentPointers: CompRef[], onSuccess?: Callback, onError?: Callback1<any>) {
    try {
        await (ps.extensionAPI.ai as AiApi).images.upscaleImages(componentPointers)
        ps.setOperationsQueue.asyncPreDataManipulationComplete()
    } catch (e: any) {
        processError(ps, e, 'UpscaleImagesError', onError)
    }
}

const api = {
    getSuggestedOutline,
    applyOutline,
    applyBuilderInjection,
    getComponentInjectionForBuilder,
    getComponentInjectionForBuilderImage,
    getComponentInjectionForBuilderImageSG,
    applyBuilderImageInjectionSG,
    getPageLinksOutline,
    getPagesLinksOutline,
    generateLinksForOutline,
    generatesLinksForOutline,
    getSuggestedSiteOutlines,
    applyOutlines,
    getSuggestedOutlineByStructure,
    getSuggestedOutlineByPageStructure,
    applyOutlineToPageStructure,
    applyOutlineToPage,
    initAITools,
    getSuggestedOutlineByStructures,
    uploadAndInjectImages,
    onGenerateSimilarImagesSuccess,
    generateSimilarImages,
    onMatchImagesToPageSuccess,
    matchImagesToPage,
    onFitImagesToFrameSuccess,
    fitImagesToFrame,
    onRemoveImagesBackgroundSuccess,
    removeImagesBackground,
    onEditImagesSuccess,
    editImages,
    onUpscaleImagesSuccess,
    upscaleImages
}

export default api
